import React, { useId } from "react";
import { normalizeStrategyFunnelData } from "../utils/strategyFunnel";
import "./StrategyFunnel.css";

const ICON_PATHS = {
  awareness: "M4 10v5h4l9 5V5L8 10H4Zm4 5 2 6M21 8c2 3 2 6 0 9",
  engagement: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M16 4a4 4 0 0 1 0 8M22 21v-2a4 4 0 0 0-3-3.87M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  ticket: "M3 6h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4V6Zm12 0v2m0 3v2m0 3v2",
  purchase: "M3 3h2l3 12h10l3-9H6M10 20h.01M18 20h.01",
  lead: "M14 3H5v18h14V8l-5-5Zm0 0v6h5M8 13h8m-8 4h5",
  traffic: "M12 3a9 9 0 1 0 9 9M12 3c-4 5-4 13 0 18M3 12h9M16 3h5v5m0-5-8 8",
  signup: "M14 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M12 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0M19 8v6m-3-3h6",
  action: "m5 12 4 4L20 5M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9",
};

function StageIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={ICON_PATHS[name] || ICON_PATHS.action} />
    </svg>
  );
}

// Purely illustrative geometry: the bands are stages of a journey, not data,
// volumes, delivery estimates, or conversion rates. All copy stays in HTML.
function FunnelIllustration({ index, id }) {
  const bands = [
    { y: 45, rx: 132, ry: 24 },
    { y: 112, rx: 88, ry: 17 },
    { y: 178, rx: 44, ry: 10 },
  ];
  return (
    <svg className="strategy-funnel__illustration" viewBox="0 0 320 258" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={`${id}-body`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#65cbff" stopOpacity=".28" />
          <stop offset=".45" stopColor="#224c8f" stopOpacity=".34" />
          <stop offset="1" stopColor="#8970ff" stopOpacity=".17" />
        </linearGradient>
        <linearGradient id={`${id}-band`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity=".7" />
          <stop offset="1" stopColor="currentColor" stopOpacity=".1" />
        </linearGradient>
        <radialGradient id={`${id}-floor`}>
          <stop offset="0" stopColor="currentColor" stopOpacity=".46" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="160" cy="240" rx="98" ry="14" fill={`url(#${id}-floor)`} />
      <path d="M28 45Q160 85 292 45L175 223v12q-15 9-30 0v-12Z" fill={`url(#${id}-body)`} stroke="#80bdff" strokeOpacity=".4" />
      {bands.map((band, bandIndex) => (
        <ellipse key={band.y} cx="160" cy={band.y} rx={band.rx} ry={band.ry}
          fill={index === bandIndex ? `url(#${id}-band)` : "#6dbeff"}
          fillOpacity={index === bandIndex ? 1 : .08}
          stroke={index === bandIndex ? "currentColor" : "#88caff"}
          strokeOpacity={index === bandIndex ? .95 : .3}
          strokeWidth={index === bandIndex ? 2 : 1} />
      ))}
      <path d="M145 235q15 9 30 0" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="160" cy={bands[index].y} r="5" fill="#f1fbff" />
      <circle cx="160" cy={bands[index].y} r="10" fill="currentColor" fillOpacity=".2" />
    </svg>
  );
}

/**
 * Reusable presentational strategy explainer. The caller owns strategy access;
 * pass null to hide it. Explicit data and derived data share the same shape.
 * @param {{data: import('../utils/strategyFunnel').StrategyFunnelData | null}} props
 */
export default function StrategyFunnel({ data }) {
  const instanceId = useId();
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const funnel = normalizeStrategyFunnelData(data);
  const headingId = `strategy-funnel-${instanceId}`;

  return (
    <section className="strategy-funnel" aria-labelledby={headingId} data-testid="strategy-funnel">
      <header className="strategy-funnel__header">
        <div>
          <span className="strategy-funnel__eyebrow">Campaign journey</span>
          <h2 id={headingId}>{funnel.headline}</h2>
        </div>
        <span className="strategy-funnel__descriptor">Audience to outcome</span>
      </header>
      <ol className="strategy-funnel__stages" aria-label="Campaign funnel stages">
        {funnel.stages.map((stage, index) => (
          <li className={`strategy-funnel__stage strategy-funnel__stage--${index + 1}`} key={index}>
            <div className="strategy-funnel__stage-copy">
              <div className="strategy-funnel__stage-heading">
                <span className="strategy-funnel__icon"><StageIcon name={stage.icon} /></span>
                <span className="strategy-funnel__number" aria-hidden="true">0{index + 1}</span>
              </div>
              <h3>{stage.title}</h3>
              <p>{stage.subtitle}</p>
            </div>
            <div className="strategy-funnel__visual">
              <FunnelIllustration index={index} id={`${headingId}-${index}`} />
              {stage.supportingLabel ? <span className="strategy-funnel__support">{stage.supportingLabel}</span> : null}
            </div>
            {index < 2 ? <span className="strategy-funnel__connector" aria-hidden="true">››</span> : null}
          </li>
        ))}
      </ol>
      {funnel.bottomJourney?.length ? (
        <ol className="strategy-funnel__journey" aria-label="Audience journey">
          {funnel.bottomJourney.map((label, index) => (
            <li key={index}><span aria-hidden="true">0{index + 1}</span>{label}</li>
          ))}
        </ol>
      ) : null}
      {funnel.endGoalLabel ? (
        <p className="strategy-funnel__goal"><span>Campaign focus</span><strong>{funnel.endGoalLabel}</strong></p>
      ) : null}
    </section>
  );
}
